import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from './firebase-init';

/**
 * Google sign-in helper — encapsulates popup → idToken → backend session.
 *
 * Backend policy (do NOT change here): /api/auth/google hardcodes role=parent
 * for all newly-provisioned users. Existing users are linked by email and
 * keep their existing role. We send the idToken; the server decides what to
 * do with it.
 *
 * This module surfaces typed errors so the UI can show a single friendly
 * message per failure mode rather than re-mapping Firebase error codes in
 * every caller.
 */

export type GoogleSignInErrorCode =
  | 'firebase_unavailable'   // VITE_FIREBASE_* env vars missing — popup can't open
  | 'popup_blocked'          // browser blocked the popup
  | 'cancelled_by_user'      // user closed popup or revoked permission
  | 'network'                // network/transport error contacting Google or our server
  | 'backend_rejected';      // /api/auth/google returned non-200 or unexpected body

export class GoogleSignInError extends Error {
  constructor(public code: GoogleSignInErrorCode, message: string) {
    super(message);
    this.name = 'GoogleSignInError';
  }
}

export type GoogleSignInResult = {
  user: {
    id: string | number;
    email: string;
    role: string;
    fullName?: string;
  };
};

const googleProvider = new GoogleAuthProvider();

/**
 * Run the full Google sign-in flow:
 *   1. Open Firebase popup (Google chooser)
 *   2. Get the user's idToken
 *   3. POST {idToken, fullName} to /api/auth/google (sets session cookie)
 *   4. Return the server-provisioned user
 *
 * Throws GoogleSignInError with a typed code on any failure. The UI should
 * surface error.message directly — copy is already user-friendly.
 */
export async function signInWithGoogleAndEstablishSession(): Promise<GoogleSignInResult> {
  // Pre-flight: Firebase initialized? If VITE_FIREBASE_* env vars aren't set,
  // firebase-init.ts leaves `auth` as null. Without it the popup cannot open,
  // so fail fast with a friendly message rather than letting Firebase throw
  // an opaque "auth/internal-error".
  if (!auth) {
    throw new GoogleSignInError(
      'firebase_unavailable',
      'Google sign-in is not available right now. Please use email and password.',
    );
  }

  // 1. Open the Google popup. signInWithPopup MUST be called synchronously
  //    from the user's click handler — any await before this point will cause
  //    some browsers to mark the popup as "not user-initiated" and block it.
  let firebaseUser;
  try {
    const result = await signInWithPopup(auth, googleProvider);
    firebaseUser = result.user;
  } catch (err: any) {
    const code = err?.code as string | undefined;
    if (code === 'auth/popup-blocked') {
      throw new GoogleSignInError(
        'popup_blocked',
        'Your browser blocked the Google sign-in popup. Please allow popups for this site and try again.',
      );
    }
    if (
      code === 'auth/popup-closed-by-user' ||
      code === 'auth/cancelled-popup-request' ||
      code === 'auth/user-cancelled'
    ) {
      throw new GoogleSignInError('cancelled_by_user', 'Sign-in was cancelled.');
    }
    if (code === 'auth/network-request-failed') {
      throw new GoogleSignInError(
        'network',
        "Couldn't reach Google. Please check your connection and try again.",
      );
    }
    // Anything else from Firebase: treat as a generic transport failure.
    throw new GoogleSignInError(
      'network',
      "Something went wrong signing in with Google. Please try again.",
    );
  }

  // 2. Get the verified Firebase idToken. This is what the server uses to
  //    confirm the user actually authenticated with Google.
  let idToken: string;
  try {
    idToken = await firebaseUser.getIdToken();
  } catch {
    throw new GoogleSignInError(
      'network',
      "Couldn't retrieve your sign-in token. Please try again.",
    );
  }

  // 3. Send the token to our backend. The server will:
  //    - verify it via Firebase Admin SDK (or local fallback in dev/test)
  //    - look up by firebase_uid → email; create as parent or link existing
  //    - regenerate the session id and set the session cookie
  //    - return the canonical user record
  let res: Response;
  try {
    res = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        idToken,
        fullName: firebaseUser.displayName ?? firebaseUser.email ?? '',
      }),
    });
  } catch {
    throw new GoogleSignInError(
      'network',
      "Couldn't reach our server. Please check your connection and try again.",
    );
  }

  if (!res.ok) {
    // 401 here means the server rejected the token (most often because
    // Firebase Admin SDK isn't configured in production and the local-decode
    // fallback refuses to run with NODE_ENV=production).
    throw new GoogleSignInError(
      'backend_rejected',
      "Sign-in failed. Please try again, or contact support if it continues.",
    );
  }

  const body = await res.json().catch(() => null);
  const user = body?.data?.user;
  if (!user || user.id == null) {
    throw new GoogleSignInError(
      'backend_rejected',
      "Sign-in succeeded but we couldn't read the response. Please refresh and try again.",
    );
  }

  return { user };
}

/**
 * Where should this user land after a successful sign-in? Per policy:
 *   - Google sign-in always provisions parents → /parent/dashboard
 *   - If the linked existing account is admin/coach/superadmin (set manually
 *     in DB later), respect that and send to /dashboard
 *   - Anything unrecognized → safe default of /parent/dashboard
 */
export function dashboardPathForRole(role: string | undefined): string {
  if (role === 'admin' || role === 'superadmin' || role === 'coach') {
    return '/dashboard';
  }
  return '/parent/dashboard';
}
